import React from 'react';
import LegalHero from '../components/LegalHero';

const TermsConditions = () => {
  const effectiveDate = 'January 15, 2026';
  const entityName = 'Nalexus Technologies (Private) Limited';
  const address = '3rd Floor, Nalexus Technologies, 12-L MM Alam Rd, Gulberg, Lahore, Punjab 54600';
  const contactEmail = 'contact@nalexustechnologies.com';
  const infoEmail = 'info@nalexustechnologies.com';
  const careerEmail = 'career@nalexustechnologies.com';
  const jurisdictionCity = 'Lahore';

  const summary = [
    { label: 'Effective date', value: effectiveDate },
    { label: 'Last updated', value: effectiveDate },
    { label: 'Company', value: entityName },
    { label: 'Jurisdiction', value: `Laws of Pakistan (${jurisdictionCity} courts)` },
    { label: 'Site contact', value: <a href={`mailto:${contactEmail}`}>{contactEmail}</a> },
    { label: 'Info & notices', value: <a href={`mailto:${infoEmail}`}>{infoEmail}</a> },
    { label: 'Careers', value: <a href={`mailto:${careerEmail}`}>{careerEmail}</a> },
  ];

  const sections = [
    {
      title: '1) About Nalexus Technologies',
      content: (
        <p>
          This Site is operated by {entityName}, registered in Pakistan with its office at {address}. You can reach the
          team at {contactEmail}, send formal information or legal notices to {infoEmail}, and direct hiring-related
          messages to {careerEmail}.
        </p>
      ),
    },
    {
      title: '2) Acceptance of these Terms',
      content: (
        <>
          <p>
            By accessing or using the Site you agree to these Terms & Conditions. If you are using the Site on behalf of
            a company, you represent that you have authority to bind that company, and "you" refers to that company.
          </p>
          <p>If you do not agree, do not use the Site.</p>
        </>
      ),
    },
    {
      title: '3) Changes to these Terms',
      content: (
        <p>We may update these Terms at any time. The updated Terms will be posted here with a new "Last updated" date. Continuing to use the Site means you accept the revised Terms.</p>
      ),
    },
    {
      title: '4) Permitted use of the Site',
      content: (
        <>
          <p>You may use the Site for lawful purposes such as learning about our services and contacting us.</p>
          <p>Do not:</p>
          <ul className="legal-list">
            <li>Attempt to gain unauthorized access to systems or data.</li>
            <li>Interfere with, disrupt, or probe the Site's security or performance.</li>
            <li>Upload or transmit malware, harmful code, spam, or deceptive messages.</li>
            <li>Scrape or harvest data without permission.</li>
            <li>Infringe intellectual property or privacy rights.</li>
            <li>Misrepresent your identity or affiliation.</li>
          </ul>
        </>
      ),
    },
    {
      title: '5) Intellectual property',
      content: (
        <p>The Site's content, layout, graphics, logos, and code are owned by Nalexus Technologies or our licensors. You may view and print content for internal business use only. Do not copy, modify, or distribute Site content without written permission.</p>
      ),
    },
    {
      title: '6) Content you submit',
      content: (
        <>
          <p>When you submit forms, uploads, or emails through the Site:</p>
          <ul className="legal-list">
            <li>You confirm you have the right to share the information.</li>
            <li>You grant us a non-exclusive, worldwide, royalty-free license to use it to respond to you and deliver requested services.</li>
            <li>You agree not to submit unlawful, infringing, or harmful content.</li>
          </ul>
          <p>Feedback or suggestions may be used without obligation or compensation.</p>
        </>
      ),
    },
    {
      title: '7) Third-party links and tools',
      content: (
        <p>The Site may link to third-party services. We do not control or endorse those services and are not responsible for their availability, content, or practices. Use of third-party services is governed by their terms.</p>
      ),
    },
    {
      title: '8) Professional services',
      content: (
        <>
          <p>Nalexus Technologies offers professional services including SaaS product engineering, CMS and digital experience platforms, enterprise applications, workflow automation, integrations, AI, and GenAI solutions.</p>
          <h5 className="legal-subheading">8.1 Separate written agreement</h5>
          <p>Services, deliverables, timelines, fees, ownership, and confidentiality are governed by written agreements (MSA/SOW/Order Form). Website content does not create a binding scope or commitment.</p>
          <h5 className="legal-subheading">8.2 Client responsibilities</h5>
          <ul className="legal-list">
            <li>Provide timely access to key stakeholders, systems, and information.</li>
            <li>Ensure you have rights to any data or materials supplied.</li>
            <li>Review deliverables and provide feedback within agreed timelines.</li>
          </ul>
          <p>Delays in inputs may affect schedules.</p>
          <h5 className="legal-subheading">8.3 Change requests</h5>
          <p>Work outside the agreed scope requires a written change order that may adjust pricing and timelines.</p>
          <h5 className="legal-subheading">8.4 Third-party services and licenses</h5>
          <p>Projects may rely on third-party platforms (hosting, analytics, payment, plugins, model providers). Unless stated otherwise, you are responsible for those subscriptions and usage costs.</p>
        </>
      ),
    },
    {
      title: '9) AI and Generative AI disclaimers',
      content: (
        <>
          <p>If the Site or a delivered solution includes AI or GenAI features:</p>
          <ul className="legal-list">
            <li>Outputs may be inaccurate, incomplete, or unsuitable for your specific use.</li>
            <li>You must review outputs before using them in production, legal, or customer-facing scenarios.</li>
            <li>If outputs trigger automated actions, implement approval and validation controls appropriate to the risk.</li>
          </ul>
          <p>Model behavior depends on data quality, integration design, and operational conditions.</p>
        </>
      ),
    },
    {
      title: '10) Disclaimers',
      content: (
        <p>The Site is provided "as is" and "as available." We do not guarantee uninterrupted, secure, or error-free operation. To the extent permitted by law we disclaim implied warranties including merchantability, fitness for a particular purpose, and non-infringement. Site content is for general information only.</p>
      ),
    },
    {
      title: '11) Limitation of liability',
      content: (
        <p>To the maximum extent permitted by law, Nalexus Technologies is not liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, revenue, opportunities, or data. Our total liability for Site-related claims is limited to the amount you paid to access the Site in the 12 months before the event, or USD $100 if you paid nothing. Service agreements may include their own limitation clause, which controls for service delivery.</p>
      ),
    },
    {
      title: '12) Indemnity',
      content: (
        <p>You agree to indemnify and hold harmless Nalexus Technologies and our team from claims, damages, liabilities, and expenses arising from your misuse of the Site, violation of these Terms or applicable law, or submission of unlawful or infringing content.</p>
      ),
    },
    {
      title: '13) Suspension and termination',
      content: (
        <p>We may suspend or terminate access if you violate these Terms, create a security risk, or if required by law. You may stop using the Site at any time.</p>
      ),
    },
    {
      title: '14) Governing law and jurisdiction',
      content: (
        <p>These Terms are governed by the laws of Pakistan. Courts located in {jurisdictionCity}, Pakistan have jurisdiction over Site-related disputes unless a separate services agreement specifies a different forum for services.</p>
      ),
    },
    {
      title: '15) Compliance with laws',
      content: (
        <p>
          You agree to comply with applicable laws, including cybersecurity, intellectual property, communications, and
          data protection laws. Pakistan's Electronic Transactions Ordinance and cybercrime framework (including PECA
          2016) are published on the official{' '}
          <a href="https://www.pakistancode.gov.pk/" target="_blank" rel="noreferrer">
            Pakistan Code
          </a>
          {' '}portal.
        </p>
      ),
    },
    {
      title: '16) Notices',
      content: (
        <>
          <p>Send legal notices to:</p>
          <ul className="legal-list">
            <li>Email: <a href={`mailto:${infoEmail}`}>{infoEmail}</a></li>
            <li>Address: {address}</li>
          </ul>
        </>
      ),
    },
    {
      title: '17) Assignment',
      content: (
        <p>You may not assign these Terms without our written consent. We may assign them in connection with a merger, acquisition, or sale of assets.</p>
      ),
    },
    {
      title: '18) Severability',
      content: (
        <p>If any provision is held unenforceable, the remaining provisions remain in effect.</p>
      ),
    },
    {
      title: '19) Force majeure',
      content: (
        <p>Neither party is liable for delays or failures caused by events beyond reasonable control, including natural disasters, war, labor disputes, internet outages, government actions, or other force majeure events.</p>
      ),
    },
    {
      title: '20) Entire agreement for Site use',
      content: (
        <p>These Terms, together with the Privacy Policy and any additional posted policies, form the entire agreement between you and Nalexus Technologies for use of the Site.</p>
      ),
    },
  ];

  return (
    <>
      <LegalHero
        eyebrow="Legal"
        title="Terms & Conditions"
        description="Rules that govern how you can use the Nalexus Technologies website, access our resources, and engage with our team online."
      />

      <section className="legal-summary sec-mar-top">
        <div className="container">
          <div className="row g-4">
            {summary.map((item) => (
              <div className="col-lg-4 col-sm-6" key={item.label}>
                <div className="legal-card legal-card--summary">
                  <p className="legal-card__label">{item.label}</p>
                  <p className="legal-card__value">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="legal-body sec-mar">
        <div className="container">
          <div className="legal-body__grid">
            {sections.map((section) => (
              <article className="legal-card" key={section.title}>
                <h4>{section.title}</h4>
                {section.content}
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsConditions;

